import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { ArrowLeft, Save, Sparkles, RefreshCw, Users, UserPlus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Per-camper carnival snack ordering for the head counselor.
 * Roster comes from the backend already filtered to kids actually at camp
 * on the configured event date.
 */
export default function CarnivalSnackOrder({ user, onBack }) {
  const token = typeof window !== "undefined" ? localStorage.getItem("token") : null;
  const headers = { Authorization: `Bearer ${token}` };
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [edits, setEdits] = useState({}); // camper_id -> flavor

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${API}/carnival-snack/roster/${encodeURIComponent(user.group_code)}`,
        { headers }
      );
      setData(res.data);
      // seed edits with saved flavors
      const seeded = {};
      for (const c of res.data.campers || []) {
        seeded[c.camper_id] = c.flavor || "";
      }
      setEdits(seeded);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load carnival roster");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (!data) return [];
    const q = search.trim().toLowerCase();
    if (!q) return data.campers;
    return data.campers.filter(c =>
      `${c.first_name} ${c.last_name}`.toLowerCase().includes(q) ||
      (c.preferred_name || "").toLowerCase().includes(q)
    );
  }, [data, search]);

  const counts = useMemo(() => {
    const out = {};
    let total = 0;
    for (const cid of Object.keys(edits)) {
      const f = edits[cid];
      if (f) { out[f] = (out[f] || 0) + 1; total += 1; }
    }
    let staffTotal = 0;
    for (const s of (data?.staff_orders || [])) {
      if (s.flavor) { out[s.flavor] = (out[s.flavor] || 0) + 1; staffTotal += 1; }
    }
    return { byFlavor: out, total, staffTotal };
  }, [edits, data]);

  const [staffName, setStaffName] = useState("");
  const [staffFlavor, setStaffFlavor] = useState("");
  const [staffBusy, setStaffBusy] = useState(false);

  const addStaff = async () => {
    if (!staffName.trim()) { toast.error("Enter the counselor's name"); return; }
    if (!staffFlavor) { toast.error("Pick a flavor"); return; }
    setStaffBusy(true);
    try {
      await axios.post(`${API}/carnival-snack/staff-order`,
        { group_code: user.group_code, name: staffName.trim(), flavor: staffFlavor },
        { headers });
      toast.success(`${staffName.trim()} added to the group order`);
      setStaffName(""); setStaffFlavor("");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to add");
    } finally { setStaffBusy(false); }
  };

  const updateStaffFlavor = async (s, flavor) => {
    try {
      await axios.put(`${API}/carnival-snack/staff-order/${s.id}`,
        { group_code: user.group_code, name: s.name, flavor },
        { headers });
      toast.success(`${s.name} → ${flavor}`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update");
    }
  };

  const removeStaff = async (s) => {
    if (!window.confirm(`Remove ${s.name} from the order?`)) return;
    try {
      await axios.delete(`${API}/carnival-snack/staff-order/${s.id}`, { headers });
      toast.success(`${s.name} removed`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to remove");
    }
  };

  const onSetFlavor = (cid, flavor) => {
    setEdits(prev => ({ ...prev, [cid]: flavor }));
  };

  const onSave = async () => {
    if (!data) return;
    setSaving(true);
    try {
      // Only push entries that DIFFER from server-side flavor — keeps the
      // payload tiny and the rejected list meaningful.
      const saved = Object.fromEntries(
        (data.campers || []).map(c => [c.camper_id, c.flavor || ""])
      );
      const orders = Object.entries(edits)
        .filter(([cid, flavor]) => (saved[cid] || "") !== (flavor || ""))
        .map(([cid, flavor]) => ({ camper_id: cid, flavor: flavor || "" }));
      if (!orders.length) { toast.info("Nothing to save."); setSaving(false); return; }
      const res = await axios.post(
        `${API}/carnival-snack/orders`,
        { group_code: user.group_code, orders },
        { headers }
      );
      const { saved: ok, removed, rejected } = res.data;
      if (rejected && rejected.length) {
        toast.warning(`Saved ${ok}, removed ${removed}, ${rejected.length} rejected`);
      } else {
        toast.success(`Saved ${ok}${removed ? ` · removed ${removed}` : ""}`);
      }
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  if (loading || !data) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-pink-50 to-amber-50 p-4 flex items-center justify-center">
        <RefreshCw className="w-6 h-6 animate-spin text-pink-600" />
      </div>
    );
  }

  if (!data.enabled) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-pink-50 to-amber-50 p-4">
        <Button variant="ghost" size="sm" onClick={onBack} className="mb-4" data-testid="carnival-back">
          <ArrowLeft className="w-4 h-4 mr-1" /> Back
        </Button>
        <Card><CardContent className="p-6 text-center text-gray-600">
          <Sparkles className="w-10 h-10 mx-auto mb-3 text-pink-400" />
          <p className="font-semibold">Carnival snack ordering is currently closed.</p>
          <p className="text-xs text-gray-500 mt-1">Check back when admin opens it.</p>
        </CardContent></Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-pink-50 to-amber-50 pb-24" data-testid="carnival-snack-order">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-white border-b shadow-sm">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={onBack} data-testid="carnival-back">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div className="flex-1 min-w-0">
            <h1 className="font-heading font-bold text-lg text-pink-700 flex items-center gap-2 truncate">
              <Sparkles className="w-5 h-5 text-amber-500" />
              Carnival {data.snack_name}
            </h1>
            <p className="text-[11px] text-gray-500">
              {user.group_name || user.group_code} · {data.event_date} · {data.total} kid{data.total !== 1 ? "s" : ""}
            </p>
          </div>
          <Button
            onClick={onSave}
            disabled={saving}
            className="bg-pink-600 hover:bg-pink-700 text-white"
            data-testid="carnival-save-btn"
          >
            <Save className="w-4 h-4 mr-1" />
            {saving ? "Saving…" : "Save"}
          </Button>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 pt-3 space-y-3">
        {/* Live totals */}
        <Card className="border-pink-200">
          <CardContent className="p-3">
            <div className="flex items-center gap-2 text-xs font-semibold text-pink-700 mb-2">
              <Users className="w-4 h-4" /> {counts.total} of {data.total} ordered
              {counts.staffTotal > 0 && (
                <span className="text-amber-700" data-testid="carnival-staff-count">
                  · {counts.staffTotal} counselor{counts.staffTotal !== 1 ? "s" : ""}
                </span>
              )}
            </div>
            <div className="flex flex-wrap gap-1.5" data-testid="carnival-flavor-tally">
              {data.flavors.map(f => (
                <span
                  key={f}
                  className="inline-flex items-center gap-1 text-xs bg-white border border-pink-200 rounded-full px-2.5 py-1"
                >
                  <span className="font-medium text-gray-800">{f}</span>
                  <span className="text-pink-600 font-bold">{counts.byFlavor[f] || 0}</span>
                </span>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Counselor orders */}
        <Card className="border-amber-300 bg-amber-50/40" data-testid="carnival-staff-section">
          <CardContent className="p-3 space-y-2">
            <div className="flex items-center gap-2 text-xs font-semibold text-amber-800">
              <UserPlus className="w-4 h-4" /> Counselor Orders
              <span className="font-normal text-gray-500">— add your staff to the group order</span>
            </div>
            {(data.staff_orders || []).map(s => (
              <div key={s.id} className="flex items-center gap-2 bg-white border border-amber-200 rounded-md px-2.5 py-1.5" data-testid={`carnival-staff-row-${s.id}`}>
                <span className="text-sm font-semibold text-gray-900 flex-1 truncate">{s.name}</span>
                <select
                  value={s.flavor || ""}
                  onChange={(e) => e.target.value && updateStaffFlavor(s, e.target.value)}
                  className="border rounded-md px-2 py-1 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-amber-400"
                  data-testid={`carnival-staff-flavor-${s.id}`}
                >
                  {data.flavors.map(f => (
                    <option key={f} value={f}>{f}</option>
                  ))}
                </select>
                <button
                  onClick={() => removeStaff(s)}
                  className="text-red-400 hover:text-red-600 p-1"
                  title={`Remove ${s.name}`}
                  data-testid={`carnival-staff-remove-${s.id}`}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
            <div className="flex items-center gap-2 flex-wrap">
              <Input
                placeholder="Counselor name…"
                value={staffName}
                onChange={(e) => setStaffName(e.target.value)}
                className="bg-white flex-1 min-w-[130px] h-9"
                data-testid="carnival-staff-name-input"
              />
              <select
                value={staffFlavor}
                onChange={(e) => setStaffFlavor(e.target.value)}
                className="border rounded-md px-2.5 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-amber-400"
                data-testid="carnival-staff-flavor-select"
              >
                <option value="">— flavor —</option>
                {data.flavors.map(f => (
                  <option key={f} value={f}>{f}</option>
                ))}
              </select>
              <Button
                size="sm"
                onClick={addStaff}
                disabled={staffBusy}
                className="bg-amber-600 hover:bg-amber-700 text-white"
                data-testid="carnival-staff-add-btn"
              >
                <UserPlus className="w-3.5 h-3.5 mr-1" />
                {staffBusy ? "Adding…" : "Add"}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Search */}
        <Input
          placeholder="Search camper…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="bg-white"
          data-testid="carnival-search"
        />

        {/* Camper list */}
        <div className="space-y-2" data-testid="carnival-camper-list">
          {filtered.length === 0 && (
            <Card><CardContent className="p-6 text-center text-sm text-gray-500">
              No campers match.
            </CardContent></Card>
          )}
          {filtered.map(c => (
            <Card key={c.camper_id} className="border-gray-200" data-testid={`carnival-row-${c.camper_id}`}>
              <CardContent className="p-3 flex items-center gap-3">
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-gray-900 truncate">
                    {c.first_name} {c.last_name}
                    {c.preferred_name && c.preferred_name !== c.first_name && (
                      <span className="ml-1 text-xs font-normal text-gray-500">
                        ({c.preferred_name})
                      </span>
                    )}
                  </div>
                </div>
                <select
                  value={edits[c.camper_id] || ""}
                  onChange={(e) => onSetFlavor(c.camper_id, e.target.value)}
                  className="border rounded-md px-2.5 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-pink-400"
                  data-testid={`carnival-flavor-${c.camper_id}`}
                >
                  <option value="">— pick —</option>
                  {data.flavors.map(f => (
                    <option key={f} value={f}>{f}</option>
                  ))}
                </select>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Sticky save bar on mobile */}
      <div className="fixed bottom-0 inset-x-0 bg-white border-t shadow-lg p-3 md:hidden">
        <Button
          onClick={onSave}
          disabled={saving}
          className="w-full bg-pink-600 hover:bg-pink-700 text-white"
          data-testid="carnival-save-btn-mobile"
        >
          <Save className="w-4 h-4 mr-1" />
          {saving ? "Saving…" : `Save · ${counts.total}/${data.total}`}
        </Button>
      </div>
    </div>
  );
}
